import React, { useEffect, useState } from 'react';
import { Upload, Button, Image, Typography, Progress, Tag, Modal, message, Space } from 'antd';
import { 
  DeleteOutlined, 
  UploadOutlined, 
  FileImageOutlined, 
  VideoCameraOutlined, 
  FileOutlined,
  PaperClipOutlined,
  LoadingOutlined,
  PlusOutlined,
  EyeOutlined,
  BookOutlined
} from '@ant-design/icons';
import MaterialSelector from '../MaterialSelector';
import MaterialUploadModal from '../MaterialUploadModal';

const MediaUpload = ({
  fieldKey,
  mediaType,
  mediaId,
  ossUrl,
  mediaId_ossUrl,
  previewUrls,
  uploading,
  uploadProgress,
  handleFileUpload,
  handleRemoveFile,
  useTempMedia = true, // 默认都使用临时素材
  stepType = 'MESSAGE',
  disabled = false, // 添加disabled参数，默认为false
  multipleMode = false, // 新增多图片模式参数
  imageGroup = null, // 新增图片组数据参数 {mediaIds: [], ossUrls: {}}
  onImageGroupChange = null, // 新增图片组变化处理函数
  // 新增全局附件信息，用于限制检查
  attachments = [],
  getTotalAttachmentCount = null,
  // 默认启用批量上传功能
  enableBatchUpload = true, // 默认启用批量上传功能
  // 素材库相关参数
  enableMaterialSelector = true, // 是否启用素材库选择
  // 素材选择回调
  onMaterialSelect = null, // 素材选择完成的回调函数
  // 新增：隐藏按钮参数
  hideRemoveButton = false, // 是否隐藏移除按钮
  hideReuploadButton = false // 是否隐藏重新上传按钮
}) => {
  // 素材选择器状态
  const [materialSelectorVisible, setMaterialSelectorVisible] = useState(false);
  
  // 素材上传Modal状态
  const [materialUploadVisible, setMaterialUploadVisible] = useState(false);
  const [uploadMaterialType, setUploadMaterialType] = useState(mediaType.toUpperCase());
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 获取租户ID
  const getTenantId = () => {
    try {
      const tenantStr = localStorage.getItem('tenant');
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        return tenant.id;
      }
    } catch (error) {
      console.error('获取租户ID失败:', error);
    }
    return null;
  };
  const [previewOssUrl, setPreviewOssUrl] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState([]); // 多图模式文件列表
  const [uploadingFiles, setUploadingFiles] = useState(new Set()); // 正在上传的文件集合

  // 确定有效的OSS URL - 从多个可能的字段中获取
  const effectiveOssUrl = ossUrl || mediaId_ossUrl;
  
  // 打印调试信息
  useEffect(() => {
    console.log(`MediaUpload组件渲染 - 类型:${mediaType}, ID:${mediaId}, OSS URL:${effectiveOssUrl}, 字段:${fieldKey}, stepType:${stepType}, 多图模式:${multipleMode}`);
    if (mediaId) {
      console.log(`MediaUpload有媒体ID:${mediaId}, 预览URL:`, previewUrls[mediaId]);
    }
    if (effectiveOssUrl) {
      console.log(`MediaUpload有有效OSS URL:${effectiveOssUrl}`);
    }
    if (multipleMode && imageGroup) {
      console.log('多图模式图片组数据:', imageGroup);
    }
  }, [mediaType, mediaId, effectiveOssUrl, previewUrls, fieldKey, stepType, multipleMode, imageGroup]);

  // 初始化多图模式的文件列表
  useEffect(() => {
    if (multipleMode && imageGroup && imageGroup.mediaIds && imageGroup.mediaIds.length > 0) {
      // 异步处理每个图片的URL
      const processFileList = async () => {
        // 创建一个临时文件列表
        const tempFileList = imageGroup.mediaIds.map((id, index) => {
          const ossUrl = imageGroup.ossUrls[id];
          return {
            uid: id,
            name: `图片${index + 1}`,
            status: 'done',
            originalUrl: ossUrl, // 保存原始OSS URL
            url: null, // 初始化为null，后续会更新
            mediaId: id,
            loading: true // 标记为加载中
          };
        });
        
        // 先设置临时列表
        setFileList(tempFileList);
        
        // 逐个处理OSS URL
        const processedFiles = await Promise.all(
          tempFileList.map(async (file) => {
            if (file.originalUrl && file.originalUrl.startsWith('oss://')) {
              try {
                // 调用预览API获取可访问的URL
                const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(file.originalUrl)}`);
                const data = await response.json();
                
                if (data.success && data.url) {
                  console.log(`成功获取图片集中图片的预览URL: ${data.url}`);
                  return { 
                    ...file, 
                    url: data.url,
                    loading: false
                  };
                }
              } catch (error) {
                console.error('获取图片预览URL失败:', error);
              }
              // 出错时保持原URL
              return { ...file, url: file.originalUrl, loading: false };
            } else if (file.originalUrl) {
              // 非OSS URL直接使用
              return { ...file, url: file.originalUrl, loading: false };
            }
            return file;
          })
        );
        
        // 更新文件列表
        setFileList(processedFiles);
        console.log('处理后的多图模式文件列表:', processedFiles);
      };
      
      processFileList();
    }
  }, [multipleMode, imageGroup]);

  // 在组件加载时获取OSS预览URL
  useEffect(() => {
    if (effectiveOssUrl) {
      setIsLoadingPreview(true);
      // 注册一个函数来获取预览URL
      const fetchPreviewUrl = async () => {
        try {
          console.log(`开始获取OSS预览URL: ${effectiveOssUrl}`);
          const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(effectiveOssUrl)}`);
          const data = await response.json();
          
          if (data.success && data.url) {
            console.log(`成功获取OSS预览URL: ${data.url}`);
            setPreviewOssUrl(data.url);
          } else {
            console.error('获取OSS预览URL失败:', data.message || '未知错误');
          }
          setIsLoadingPreview(false);
        } catch (error) {
          console.error('获取OSS预览URL异常:', error);
          setIsLoadingPreview(false);
        }
      };
      
      fetchPreviewUrl();
    }
  }, [effectiveOssUrl]);
  
  // 处理mediaId预览URL
  useEffect(() => {
    if (mediaId && previewUrls[mediaId] && !effectiveOssUrl) {
      // 如果是外部URL，不需要通过后端转换
      if (previewUrls[mediaId].startsWith('http')) {
        setMediaPreviewUrl(previewUrls[mediaId]);
      } else if (previewUrls[mediaId].startsWith('oss://')) {
        // 是OSS URL，需要转换
        setIsLoadingPreview(true);
        const fetchMediaPreviewUrl = async () => {
          try {
            console.log(`尝试获取mediaId(${mediaId})对应的预览URL`, previewUrls[mediaId]);
            const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(previewUrls[mediaId])}`);
            const data = await response.json();
            
            if (data.success && data.url) {
              console.log(`成功获取mediaId预览URL: ${data.url}`);
              setMediaPreviewUrl(data.url);
            } else {
              console.error('获取mediaId预览URL失败:', data.message || '未知错误');
              // 失败时回退到原始URL
              setMediaPreviewUrl(previewUrls[mediaId]);
            }
            setIsLoadingPreview(false);
          } catch (error) {
            console.error('获取mediaId预览URL异常:', error);
            setMediaPreviewUrl(previewUrls[mediaId]); // 失败时回退到原始URL
            setIsLoadingPreview(false);
          }
        };
        
        fetchMediaPreviewUrl();
      } else {
        // 其他情况，直接使用预览URL
        setMediaPreviewUrl(previewUrls[mediaId]);
      }
    }
    // 即使没有预览URL，也应该添加这个mediaId到调试日志
    else if (mediaId && fieldKey === 'thumbMediaId') {
      console.log(`小程序缩略图 - mediaId: ${mediaId}, 但没有找到预览URL或effectiveOssUrl已存在`);
    }
  }, [mediaId, previewUrls, effectiveOssUrl, fieldKey]);

  // 新增：当素材选择导致mediaId和ossUrl同时更新时的处理
  useEffect(() => {
    if (mediaId && effectiveOssUrl && !previewOssUrl && !isLoadingPreview) {
      console.log(`检测到素材选择更新，mediaId: ${mediaId}, ossUrl: ${effectiveOssUrl}`);
      // 触发OSS URL预览获取
      setIsLoadingPreview(true);
      const fetchPreviewUrl = async () => {
        try {
          console.log(`开始获取素材OSS预览URL: ${effectiveOssUrl}`);
          const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(effectiveOssUrl)}`);
          const data = await response.json();
          
          if (data.success && data.url) {
            console.log(`成功获取素材OSS预览URL: ${data.url}`);
            setPreviewOssUrl(data.url);
          } else {
            console.error('获取素材OSS预览URL失败:', data.message || '未知错误');
          }
          setIsLoadingPreview(false);
        } catch (error) {
          console.error('获取素材OSS预览URL异常:', error);
          setIsLoadingPreview(false);
        }
      };
      
      fetchPreviewUrl();
    }
  }, [mediaId, effectiveOssUrl, previewOssUrl, isLoadingPreview]);

  // 判断是否为朋友圈附件
  const isMomentAttachment = stepType === 'MOMENTS';
  
  // 判断是否有媒体 (优先检查mediaId，其次是ossUrl)
  const hasMedia = !multipleMode && (mediaId || effectiveOssUrl);
  
  // 上传进度和状态 - 使用复合键来区分不同步骤类型的上传
  const uploadKey = `${stepType}_${fieldKey}`;
  const isUploading = uploading[uploadKey] || uploading[fieldKey];
  const progress = uploadProgress[uploadKey] || uploadProgress[fieldKey] || 0;

  // 显示友好的媒体ID
  const shortMediaId = mediaId ? 
    (mediaId.length > 8 ? `${mediaId.substring(0, 8)}...` : mediaId) 
    : '无';

  // 获取可用的预览URL - 优先使用OSS URL
  const getPreviewUrl = () => {
    if (effectiveOssUrl) {
      // 优先使用已获取的预览URL
      if (previewOssUrl) {
        return previewOssUrl;
      }
      
      // 如果还没获取到预览URL，返回null表示正在加载
      if (isLoadingPreview) {
        console.log(`正在加载OSS预览URL...`);
        return null;
      }
      
      // 构建OSS预览URL（这是后备方案）
      const encodedOssUrl = encodeURIComponent(effectiveOssUrl);
      const previewUrl = `/api/media/wework/oss/preview?url=${encodedOssUrl}`;
      console.log(`构建预览URL: ${previewUrl} (原始OSS URL: ${effectiveOssUrl})`);
      return previewUrl;
    }
    
    // 如果有mediaId，使用对应的预览URL
    if (mediaId && previewUrls[mediaId]) {
      // 如果已经获取到转换后的URL，直接使用
      if (mediaPreviewUrl) {
        console.log(`使用已获取的mediaId预览URL: ${mediaPreviewUrl}`);
        return mediaPreviewUrl;
      }
      
      // 如果正在加载预览URL，返回null
      if (isLoadingPreview) {
        console.log(`正在加载mediaId预览URL...`);
        return null;
      }
      
      // 没有经过处理的URL，直接返回原始的(可能需要转换)
      return previewUrls[mediaId];
    }
    
    return null;
  };

  // 处理多图模式的文件上传
  const handleMultipleUpload = async (options) => {
    console.log('多图模式文件上传:', options);
    const { file, onSuccess, onError, onProgress } = options;
    
    try {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        message.error('只能上传图片文件');
        if (onError) onError(new Error('只能上传图片文件'));
        return;
      }
      
      // 检查文件大小 (10MB限制)
      if (file.size > 10 * 1024 * 1024) {
        message.error('图片大小不能超过10MB');
        if (onError) onError(new Error('图片大小不能超过10MB'));
        return;
      }
      
      // 使用全局附件计数检查限制
      if (getTotalAttachmentCount) {
        const currentTotal = getTotalAttachmentCount();
        if (currentTotal >= 9) {
          message.error(`最多只能添加9个附件，当前已有${currentTotal}个附件`);
          if (onError) onError(new Error('附件数量超限'));
          return;
        }
      } else {
        // 备用：检查当前图片组是否已达到9张限制（如果没有全局计数函数）
        const currentImageCount = imageGroup?.mediaIds?.length || 0;
        if (currentImageCount >= 9) {
          message.error('图片组最多只能上传9张图片');
          if (onError) onError(new Error('图片组数量超限'));
          return;
        }
      }
      
      // 标记文件正在上传
      setUploadingFiles(prev => new Set([...prev, file.uid]));
      
      // 使用现有的handleFileUpload处理上传
      const result = await handleFileUpload({
        file,
        onSuccess: async (uploadResult) => {
          console.log('多图模式上传成功:', uploadResult);
          
          try {
            // 处理OSS URL转换为可访问的预览URL
            let previewUrl = uploadResult.ossUrl;
            
            // 如果是OSS URL，请求转换
            if (uploadResult.ossUrl && uploadResult.ossUrl.startsWith('oss://')) {
              try {
                const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(uploadResult.ossUrl)}`);
                const data = await response.json();
                
                if (data.success && data.url) {
                  console.log(`上传成功后获取预览URL: ${data.url}`);
                  previewUrl = data.url;
                }
              } catch (error) {
                console.error('获取上传图片预览URL失败:', error);
              }
            }
            
            // 创建新文件对象
            const newFile = {
              uid: uploadResult.mediaId,
              name: file.name,
              status: 'done',
              url: previewUrl,
              originalUrl: uploadResult.ossUrl,
              mediaId: uploadResult.mediaId,
              ossUrl: uploadResult.ossUrl
            };
            
            // 更新文件列表
            setFileList(prev => [...prev, newFile]);
            
            // 更新图片组数据
            if (onImageGroupChange) {
              // 构建新的mediaIds和ossUrls
              const newMediaIds = [...(imageGroup?.mediaIds || []), uploadResult.mediaId];
              const newOssUrls = { ...(imageGroup?.ossUrls || {}) };
              newOssUrls[uploadResult.mediaId] = uploadResult.ossUrl;
              
              onImageGroupChange({
                mediaIds: newMediaIds,
                ossUrls: newOssUrls
              });
            }
            
            // 移除上传状态标记
            setUploadingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.uid);
              return newSet;
            });
            
            // 调用原始成功回调
            if (onSuccess) onSuccess(uploadResult);
          } catch (error) {
            console.error('处理上传结果时出错:', error);
            setUploadingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.uid);
              return newSet;
            });
            if (onError) onError(error);
          }
        },
        onError: (error) => {
          console.error('多图模式上传失败:', error);
          setUploadingFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(file.uid);
            return newSet;
          });
          if (onError) onError(error);
        },
        onProgress,
        fieldKey,
        mediaType,
        useTempMedia: true,
        stepType
      });
      
      return result;
    } catch (error) {
      console.error('图片上传失败:', error);
      setUploadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.uid);
        return newSet;
      });
      if (onError) onError(error);
    }
  };

  // 处理多图模式的文件移除
  const handleMultipleRemove = (file) => {
    console.log('多图模式文件移除:', file);
    
    // 从文件列表中移除
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    
    // 更新图片组数据
    if (onImageGroupChange && imageGroup) {
      // 找到要删除的图片在mediaIds中的索引位置（基于fileList中的顺序）
      const fileIndex = fileList.findIndex(item => item.uid === file.uid);
      
      if (fileIndex !== -1 && imageGroup.mediaIds && fileIndex < imageGroup.mediaIds.length) {
        // 从mediaIds中移除对应位置的单个元素
        const newMediaIds = [...imageGroup.mediaIds];
        const removedMediaId = newMediaIds.splice(fileIndex, 1)[0];
        
        // 检查是否还有其他图片使用相同的mediaId
        const stillHasSameMediaId = newMediaIds.includes(removedMediaId);
        
        // 从ossUrls中移除（只有当没有其他图片使用相同mediaId时才删除）
        const newOssUrls = { ...imageGroup.ossUrls };
        if (!stillHasSameMediaId && removedMediaId) {
          delete newOssUrls[removedMediaId];
        }
        
        onImageGroupChange({
          mediaIds: newMediaIds,
          ossUrls: newOssUrls
        });
        
        // 调用原始移除处理函数，传递要移除的mediaId
        if (handleRemoveFile) {
          handleRemoveFile(fieldKey, stepType, removedMediaId);
        }
      }
    }
  };

  // 处理预览
  const handlePreview = async (file) => {
    console.log('预览文件:', file);
    
    if (!file.url && !file.preview) {
      // 如果是新上传的文件，从 originFileObj 创建预览
      if (file.originFileObj) {
        file.preview = await new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj);
          reader.onload = () => resolve(reader.result);
        });
      } 
      // 如果是已有文件但没有可访问的URL，尝试获取预览URL
      else if (file.originalUrl && file.originalUrl.startsWith('oss://')) {
        try {
          const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(file.originalUrl)}`);
          const data = await response.json();
          
          if (data.success && data.url) {
            console.log(`预览时成功获取图片URL: ${data.url}`);
            file.preview = data.url;
          }
        } catch (error) {
          console.error('预览时获取图片URL失败:', error);
        }
      }
    }
    
    // 优先使用已有URL，其次使用预览URL
    const previewUrl = file.url || file.preview;
    if (previewUrl) {
      setPreviewImage(previewUrl);
      setPreviewVisible(true);
    } else {
      console.error('无法获取预览图片URL');
    }
  };

  // 多图模式的上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>添加图片</div>
    </div>
  );

  // 处理批量上传
  const handleBatchUpload = async (files) => {
    console.log('批量上传文件:', files);
    
    if (!files || files.length === 0) return;
    
    // 检查文件数量限制
    if (getTotalAttachmentCount) {
      const currentTotal = getTotalAttachmentCount();
      if (currentTotal + files.length > 9) {
        message.error(`最多只能添加9个附件，当前已有${currentTotal}个附件，无法再添加${files.length}个文件`);
        return;
      }
    } else if (files.length > 9) {
      message.error('一次最多只能上传9张图片');
      return;
    }

    const formData = new FormData();
    
    // 添加所有文件到FormData
    Array.from(files).forEach((file, index) => {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        message.error(`文件 "${file.name}" 不是图片格式`);
        return;
      }
      
      // 检查文件大小
      if (file.size > 10 * 1024 * 1024) {
        message.error(`文件 "${file.name}" 大小超过10MB限制`);
        return;
      }
      
      formData.append('files', file);
    });
    
    // 获取tenantId - 优先从URL参数获取，其次从localStorage
    let tenantId = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlTenantId = urlParams.get('tenantId');
      if (urlTenantId) {
        tenantId = urlTenantId;
      } else {
        // 尝试从localStorage获取
        const storedTenantId = localStorage.getItem('tenantId');
        if (storedTenantId) {
          tenantId = storedTenantId;
        }
      }
    } catch (error) {
      console.warn('获取tenantId失败:', error);
    }
    
    // 如果没有tenantId，停止上传
    if (!tenantId) {
      message.error('未找到租户信息，无法上传文件');
      return;
    }
    
    // 添加其他参数
    formData.append('mediaType', mediaType);
    formData.append('tenantId', tenantId);
    formData.append('sceneType', stepType === 'MOMENTS' ? 'moment' : 'message');

    try {
      // 显示上传中状态
      const uploadingSet = new Set();
      Array.from(files).forEach((file, index) => {
        uploadingSet.add(file.name + index);
      });
      setUploadingFiles(uploadingSet);

      const response = await fetch('/api/media/wework/upload_batch', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      console.log('批量上传结果:', result);

      if (result.success && result.successCount > 0) {
        message.success(`批量上传完成，成功: ${result.successCount}个，失败: ${result.failureCount}个`);
        
        // 处理成功上传的文件
        if (result.results && result.results.length > 0) {
          const newFiles = [];
          
          for (const fileResult of result.results) {
            if (fileResult.success) {
              // 获取预览URL
              let previewUrl = fileResult.ossUrl;
              
              if (fileResult.ossUrl && fileResult.ossUrl.startsWith('oss://')) {
                try {
                  const previewResponse = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(fileResult.ossUrl)}`);
                  const previewData = await previewResponse.json();
                  
                  if (previewData.success && previewData.url) {
                    previewUrl = previewData.url;
                  }
                } catch (error) {
                  console.error('获取预览URL失败:', error);
                }
              }
              
              const newFile = {
                uid: fileResult.mediaId,
                name: fileResult.fileName,
                status: 'done',
                url: previewUrl,
                originalUrl: fileResult.ossUrl,
                mediaId: fileResult.mediaId,
                ossUrl: fileResult.ossUrl
              };
              
              newFiles.push(newFile);
            }
          }
          
          // 更新文件列表
          setFileList(prev => [...prev, ...newFiles]);
          
          // 更新图片组数据 - 修复：确保追加而不是替换
          if (onImageGroupChange) {
            // 始终构建追加的图片组数据，不直接使用后端返回的imageGroup
            const newImageGroup = {
              mediaIds: [...(imageGroup?.mediaIds || []), ...result.mediaIds],
              ossUrls: { ...(imageGroup?.ossUrls || {}), ...result.ossUrls }
            };
            console.log('批量上传后更新图片组数据:', newImageGroup);
            onImageGroupChange(newImageGroup);
          }
        }
      } else {
        message.error(result.message || '批量上传失败');
      }
    } catch (error) {
      console.error('批量上传异常:', error);
      message.error('批量上传失败: ' + error.message);
    } finally {
      // 清除上传状态
      setUploadingFiles(new Set());
    }
  };

  // 处理单图模式的多文件选择（改进版本）
  const handleSingleModeMultipleFiles = async (fileList) => {
    console.log('单图模式检测到多文件选择:', fileList);
    
    if (fileList.length > 1) {
      if (enableBatchUpload && mediaType === 'image') {
        // 如果启用了批量上传且是图片类型，直接批量上传
        console.log('自动启用批量上传模式');
        handleBatchUpload(fileList);
        return;
      } else {
        message.warning(`检测到选择了${fileList.length}个文件，在单图模式下只会上传第一个文件`);
      }
    }
    
    // 只处理第一个文件
    uploadSingleFile(fileList[0]);
  };

  // 上传单个文件的辅助函数
  const uploadSingleFile = (file) => {
    if (!file) return;
    
    // 检查文件类型
    if (mediaType === 'image' && !file.type.startsWith('image/')) {
      message.error('只能上传图片文件');
      return;
    }
    
    if (mediaType === 'video' && !file.type.startsWith('video/')) {
      message.error('只能上传视频文件');
      return;
    }
    
    // 检查文件大小
    if (file.size > 10 * 1024 * 1024) {
      message.error('文件大小不能超过10MB');
      return;
    }
    
    // 创建文件上传选项
    const options = {
      file,
      onSuccess: (result) => {
        console.log('单图模式文件上传成功:', result);
      },
      onError: (error) => {
        console.error('单图模式文件上传失败:', error);
        message.error('文件上传失败: ' + error.message);
      },
      onProgress: (progress) => {
        console.log('单图模式上传进度:', progress);
      }
    };
    
    // 调用原有的handleFileUpload
    return handleFileUpload({
      ...options,
      fieldKey,
      mediaType,
      useTempMedia: true,
      stepType
    });
  };

  // 多图模式渲染
  if (multipleMode) {
    return (
      <div className="multiple-upload-container">
        <Upload
          listType="picture-card"
          fileList={fileList}
          customRequest={handleMultipleUpload}
          beforeUpload={(file, fileList) => {
            // 检查文件类型
            if (!file.type.startsWith('image/')) {
              message.error('只能上传图片文件');
              return Upload.LIST_IGNORE;
            }
            
            // 检查文件大小 (10MB限制)
            if (file.size > 10 * 1024 * 1024) {
              message.error('图片大小不能超过10MB');
              return Upload.LIST_IGNORE;
            }
            
            // 使用全局附件计数检查限制
            if (getTotalAttachmentCount) {
              const currentTotal = getTotalAttachmentCount();
              if (currentTotal >= 9) {
                message.error(`最多只能添加9个附件，当前已有${currentTotal}个附件`);
                return Upload.LIST_IGNORE;
              }
            } else {
              // 备用：检查当前图片组是否已达到9张限制（如果没有全局计数函数）
              const currentImageCount = imageGroup?.mediaIds?.length || 0;
              if (currentImageCount >= 9) {
                message.error('图片组最多只能上传9张图片');
                return Upload.LIST_IGNORE;
              }
            }
            
            // 如果选择了多个文件，使用批量上传
            if (enableBatchUpload && fileList.length > 1) {
              console.log('检测到多文件选择，启用批量上传');
              handleBatchUpload(fileList);
              return false; // 阻止默认上传
            }
            
            return true;
          }}
          onPreview={handlePreview}
          onRemove={handleMultipleRemove}
          disabled={disabled}
          accept="image/*"
          multiple={true}
        >
          {fileList.length >= 9 ? null : uploadButton}
        </Upload>
        
        <Modal
          visible={previewVisible}
          title="图片预览"
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img 
            alt="预览图片" 
            style={{ width: '100%' }} 
            src={previewImage} 
            onError={(e) => {
              console.error('预览图片加载失败:', previewImage);
              e.target.onerror = null;
              // 如果图片加载失败，显示一个占位图像
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
              e.target.style.padding = '100px';
              e.target.style.background = '#f0f0f0';
            }}
          />
        </Modal>
        
      </div>
    );
  }

  // 单图模式渲染（原有逻辑）
  return (
    <div className="custom-upload-container">
      {hasMedia ? (
        <div className="media-preview">
          {mediaType === 'image' ? (
            <div className="image-preview-wrapper">
              {effectiveOssUrl || mediaId ? (
                // 使用OSS URL或mediaId预览图片（修改条件：只要有ossUrl或mediaId就尝试显示）
                <>
                  {(previewOssUrl || mediaPreviewUrl) ? (
                    // 如果已经获取到了预览URL，直接显示
                    <img
                      src={getPreviewUrl()}
                      alt="预览" 
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                      onLoad={(e) => console.log('图片加载成功:', e.target.src)}
                      onError={(e) => {
                        console.error('图片加载失败:', e.target.src);
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                        e.target.style.padding = '50px';
                        e.target.style.background = '#f0f0f0';
                      }}
                    />
                  ) : (
                    // 正在加载预览URL，显示加载中状态
                    <div style={{
                      height: 200,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: '#f9f9f9',
                      border: '1px dashed #d9d9d9',
                      borderRadius: 4
                    }}>
                      加载图片预览中...
                    </div>
                  )}
                </>
              ) : (
                // 预览占位符
                <div style={{ 
                  background: '#f5f5f5', 
                  height: 150, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  border: '1px dashed #d9d9d9',
                  borderRadius: 4
                }}>
                  <FileImageOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                  <div>{isMomentAttachment ? '朋友圈图片素材 (上传成功)' : '图片素材 (上传成功)'}</div>
                  {mediaId && (
                    <div style={{ marginTop: 8, color: '#389e0d', fontWeight: 'bold' }}>
                      ID: {shortMediaId}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : mediaType === 'video' ? (
            <div className="video-preview-wrapper">
              {effectiveOssUrl || mediaId ? (
                // 如果有OSS URL或mediaId，尝试直接预览视频
                <div style={{ 
                  background: '#f5f5f5', 
                  minHeight: 150, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  border: '1px dashed #d9d9d9',
                  borderRadius: 4,
                  padding: '16px 0'
                }}>
                  {getPreviewUrl() ? (
                    <video 
                      controls 
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                      src={getPreviewUrl()}
                    >
                      您的浏览器不支持视频播放
                    </video>
                  ) : (
                    <div style={{ padding: '30px 20px', textAlign: 'center' }}>
                      正在加载视频预览，请稍候...
                    </div>
                  )}
                  <Typography.Text strong style={{ marginTop: 8 }}>
                    视频已上传{effectiveOssUrl ? '(OSS存储)' : ''}
                  </Typography.Text>
                </div>
              ) : (
                <div style={{ 
                  background: '#f5f5f5', 
                  minHeight: 150, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  border: '1px dashed #d9d9d9',
                  borderRadius: 4,
                  padding: '16px 0'
                }}>
                  <VideoCameraOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                  <div style={{ textAlign: 'center', padding: '0 16px', fontWeight: 'bold' }}>
                    {isMomentAttachment ? 
                      '朋友圈视频素材' : 
                      '视频已上传，发送后对方可查看'
                    }
                  </div>
                  {mediaId && (
                    <div style={{ marginTop: 8, color: '#389e0d', fontWeight: 'bold' }}>
                      ID: {shortMediaId}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="preview-placeholder" style={{ 
              background: '#f5f5f5', 
              padding: 16,
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px dashed #d9d9d9',
              borderRadius: 4
            }}>
              <FileOutlined style={{ fontSize: 32, marginBottom: 8 }} />
              <div>文件上传成功</div>
              {mediaId && <div>媒体ID: {shortMediaId}</div>}
            </div>
          )}
          {(!hideRemoveButton || !hideReuploadButton) && (
            <div className="preview-actions" style={{ marginTop: 8 }}>
              {!hideRemoveButton && (
                <Button 
                  icon={<DeleteOutlined />} 
                  onClick={() => {
                    console.log('点击移除按钮, 字段:', fieldKey, 'mediaId:', mediaId, 'ossUrl:', effectiveOssUrl, 'mediaType:', mediaType, 'stepType:', stepType);
                    handleRemoveFile(fieldKey, stepType);
                  }}
                  danger
                  disabled={disabled}
                >
                  移除
                </Button>
              )}
              {!hideReuploadButton && (
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => {
                    console.log('点击重新上传按钮, 字段:', fieldKey, 'mediaId:', mediaId, 'ossUrl:', effectiveOssUrl, 'mediaType:', mediaType, 'stepType:', stepType);
                    handleRemoveFile(fieldKey, stepType);
                  }}
                  style={{ marginLeft: hideRemoveButton ? 0 : 8 }}
                  disabled={disabled}
                >
                  重新上传
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <Upload.Dragger
          name="file"
          multiple={true} // 改为允许多选
          customRequest={(options) => {
            console.log('开始上传文件, 字段:', fieldKey, 'mediaType:', mediaType, 'stepType:', stepType);
            const result = handleFileUpload({ 
              ...options, 
              fieldKey, 
              mediaType, 
              useTempMedia: true, // 强制使用临时素材 
              stepType 
            });
            console.log('上传文件请求发送完成, 结果:', result);
            return result;
          }}
          beforeUpload={(file, fileList) => {
            // 在单图模式下处理多文件选择
            if (fileList.length > 1) {
              console.log('单图模式检测到多文件选择，文件数量:', fileList.length);
              handleSingleModeMultipleFiles(fileList);
              // 阻止默认的上传行为，因为我们手动处理了
              return false;
            }
            return true;
          }}
          showUploadList={false}
          disabled={isUploading || disabled}
          accept={
            mediaType === 'image' ? 'image/*' : 
            mediaType === 'video' ? 'video/*' : 
            '.pdf,.doc,.docx,.xls,.xlsx,.txt'
          }
        >
          {isUploading ? (
            <div className="uploading-container">
              <LoadingOutlined style={{ fontSize: 24, marginBottom: 12 }} />
              <Progress percent={progress} status="active" />
              <p className="ant-upload-hint">正在上传{mediaType === 'video' ? '视频' : mediaType === 'image' ? '图片' : '文件'}，请稍候...</p>
            </div>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                {mediaType === 'image' ? <FileImageOutlined /> : 
                 mediaType === 'video' ? <VideoCameraOutlined /> : 
                 <PaperClipOutlined />}
              </p>
              <p className="ant-upload-text">点击或拖拽{mediaType === 'video' ? '视频' : mediaType === 'image' ? '图片' : '文件'}到此区域上传</p>
              <p className="ant-upload-hint">
                {mediaType === 'image' && (isMomentAttachment ? 
                  '支持 JPG、PNG 格式图片，大小不超过10MB，长边不超过10800像素，短边不超过1080像素' : 
                  '支持 JPG、PNG 格式图片，大小不超过10MB，支持同时选择多张图片批量上传')}
                {mediaType === 'video' && (isMomentAttachment ? 
                  '支持 MP4 格式视频，大小不超过10MB，时长不超过30秒' : 
                  '支持 MP4 格式视频，大小不超过10MB')}
                {mediaType === 'file' && '支持 PDF、DOC、DOCX 等格式文件'}
              </p>
              <div style={{ marginTop: 10, fontSize: 12, color: '#52c41a' }}>
                文件将保存在云存储中
              </div>
            </>
          )}
        </Upload.Dragger>
      )}
      
      {/* 素材库选择按钮 - 仅在MaterialSelector中显示，其他情况不显示 */}
      {enableMaterialSelector && !hasMedia && !isUploading && !disabled && (stepType === 'MATERIAL' || stepType === 'MESSAGE' || stepType === 'MOMENTS') && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button 
            type="link"
            icon={<BookOutlined />}
            onClick={() => setMaterialSelectorVisible(true)}
            style={{ padding: 0 }}
          >
            从素材库选择
          </Button>
        </div>
      )}
      
      {/* 素材选择器 */}
      <MaterialSelector
        key="mediaupload-selector"
        visible={materialSelectorVisible}
        onCancel={() => setMaterialSelectorVisible(false)}
        onSelect={handleMaterialSelect}
        materialType={mediaType.toUpperCase()}
        hideAllTypesTab={false}
        multiSelect={multipleMode}
        title={`选择${mediaType === 'image' ? '图片' : mediaType === 'video' ? '视频' : '文件'}素材`}
        allowUpload={true}
        onUpload={(selectedMaterialType) => {
          setUploadMaterialType(selectedMaterialType || mediaType.toUpperCase());
          setMaterialUploadVisible(true);
        }}
        refreshTrigger={refreshTrigger}
        tenantId={getTenantId()}
      />
      
      {/* 素材上传Modal */}
      <MaterialUploadModal
        visible={materialUploadVisible}
        onCancel={() => setMaterialUploadVisible(false)}
        onSuccess={() => {
          // 上传成功后关闭Modal并刷新素材列表
          setMaterialUploadVisible(false);
          // 触发MaterialSelector刷新
          setRefreshTrigger(prev => prev + 1);
        }}
        initialType={uploadMaterialType}
        hideTypeSelection={true}
        tenantId={getTenantId()}
      />
    </div>
  );

  // 处理素材选择
  function handleMaterialSelect(material) {
    console.log('MediaUpload选择素材:', material);
    
    try {
      if (multipleMode && material.length > 0) {
        // 多选模式，处理图片组
        const newImageGroup = {
          mediaIds: [],
          ossUrls: {}
        };
        
        // 构建图片组数据
        material.forEach(m => {
          if (m.imageInfo?.mediaId && m.imageInfo?.url) {
            newImageGroup.mediaIds.push(m.imageInfo.mediaId);
            newImageGroup.ossUrls[m.imageInfo.mediaId] = m.imageInfo.url;
          }
        });
        
        console.log('素材库多选，构建图片组:', newImageGroup);
        
        // 更新图片组
        if (onImageGroupChange && newImageGroup.mediaIds.length > 0) {
          // 合并现有图片组和新选择的图片
          const currentGroup = imageGroup || { mediaIds: [], ossUrls: {} };
          const mergedGroup = {
            mediaIds: [...currentGroup.mediaIds, ...newImageGroup.mediaIds],
            ossUrls: { ...currentGroup.ossUrls, ...newImageGroup.ossUrls }
          };
          console.log('合并后的图片组:', mergedGroup);
          onImageGroupChange(mergedGroup);
        }
        
        // 调用专门的素材选择回调
        if (onMaterialSelect) {
          onMaterialSelect({
            type: 'multiple',
            materials: material,
            imageGroup: newImageGroup,
            fieldKey,
            stepType
          });
        }
      } else {
        // 单选模式
        const selectedMaterial = Array.isArray(material) ? material[0] : material;
        
        console.log('素材库单选:', selectedMaterial);
        
        // 构建统一的结果对象
        let materialResult = null;
        
        if (mediaType === 'image' && selectedMaterial.imageInfo) {
          materialResult = {
            mediaId: selectedMaterial.imageInfo.mediaId,
            ossUrl: selectedMaterial.imageInfo.url,
            success: true,
            fileName: selectedMaterial.name || '素材图片',
            size: 0,
            type: 'image',
            materialType: 'IMAGE',
            fieldKey,
            stepType
          };
        } else if (mediaType === 'video' && selectedMaterial.videoInfo) {
          materialResult = {
            mediaId: selectedMaterial.videoInfo.mediaId,
            ossUrl: selectedMaterial.videoInfo.url,
            success: true,
            fileName: selectedMaterial.name || '素材视频',
            size: 0,
            type: 'video',
            materialType: 'VIDEO',
            fieldKey,
            stepType
          };
        }
        
        if (materialResult) {
          console.log('素材选择结果:', materialResult);
          
          // 优先使用专门的素材选择回调
          if (onMaterialSelect) {
            console.log('使用专门的素材选择回调处理');
            onMaterialSelect({
              type: 'single',
              material: selectedMaterial,
              result: materialResult,
              fieldKey,
              stepType
            });
          } else if (handleFileUpload) {
            // 回退到模拟文件上传的方式
            console.log('回退到模拟文件上传方式处理素材选择');
            
            // 直接调用成功回调，传递素材结果
            setTimeout(() => {
              const uploadOptions = {
                file: {
                  name: materialResult.fileName,
                  type: materialResult.type === 'image' ? 'image/jpeg' : 'video/mp4',
                  size: 0
                },
                onSuccess: (result) => {
                  console.log('素材选择成功回调被调用:', result);
                },
                onError: (error) => {
                  console.error('素材选择处理失败:', error);
                  message.error('素材选择处理失败');
                },
                onProgress: () => {},
                fieldKey,
                mediaType,
                useTempMedia: true,
                stepType
              };
              
              // 模拟上传成功，直接调用成功回调
              uploadOptions.onSuccess(materialResult);
              
              // 对于多图模式，还需要更新图片组
              if (onImageGroupChange && multipleMode) {
                const imageGroup = {
                  mediaIds: [materialResult.mediaId],
                  ossUrls: {
                    [materialResult.mediaId]: materialResult.ossUrl
                  }
                };
                onImageGroupChange(imageGroup);
              }
            }, 100);
          } else {
            console.warn('没有找到合适的回调函数处理素材选择');
          }
        }
      }
      
      setMaterialSelectorVisible(false);
      message.success('素材选择成功');
      
    } catch (error) {
      console.error('处理素材选择失败:', error);
      message.error('选择素材失败，请重试');
    }
  }
};

export default MediaUpload; 